import React, { useCallback, useEffect, useRef, useState } from 'react';
import { authFetch, API } from '../api';
import { useReprintApproval } from './ReprintApproval';
import { useScanWedge } from '../scanWedge';

// Bring boxes for the HRX send at a P&P station (guided P&P phase 9a, 2026-10-18; backend/ship_bulks.py). A box per
// country with our box label (printed on this station's Zebra); scan the box label, then every Bring parcel that goes
// in it. Fernando: the biggest mistake to prevent is a DK parcel ending up in Norway — a parcel for another country is
// stopped full screen and has to be acknowledged. "HRX leaves" closes the shipment.

interface Box { id: number; country: string; box_no: number; barcode: string; parcels: number }
interface Missing { order_id: string; order_number: string; country: string }
interface State {
  bulk: { code: string } | null; boxes: Box[]; per_country: Record<string, number>; count: number;
  items: { id: number; order_number: string; dest_country: string; box_id: number }[]; missing: Missing[];
  /** parcels boxed while their round ran that must come out again (3.11) */
  problems?: { item_id: number; order_number: string; why: string }[];
}

const COUNTRIES: [string, string][] = [['SE', 'Sweden'], ['DK', 'Denmark'], ['NO', 'Norway']];
const COLOR: Record<string, string> = { SE: 'border-sky-500 bg-sky-900/25', DK: 'border-rose-500 bg-rose-900/25', NO: 'border-amber-500 bg-amber-900/25' };

async function detailOf(r: Response): Promise<{ text: string; wrong: boolean }> {
  const d = await r.json().catch(() => ({} as any));
  if (d && typeof d.detail === 'object' && d.detail) return { text: d.detail.message || `HTTP ${r.status}`, wrong: !!d.detail.wrong_country };
  return { text: typeof d.detail === 'string' ? d.detail : `HTTP ${r.status}`, wrong: false };
}

export default function HrxBoxes({ onBack }: { onBack: () => void }) {
  const [st, setSt] = useState<State | null>(null);
  const [active, setActive] = useState<Box | null>(null);
  const [last, setLast] = useState<{ ok: boolean; text: string } | null>(null);
  const [stop, setStop] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<any>(null);
  const reprintApproval = useReprintApproval();   // a box label again: Tier 1 calls a manager (19.10)
  const activeRef = useRef<Box | null>(null);
  activeRef.current = active;
  const stopRef = useRef<string | null>(null);
  stopRef.current = stop;

  const load = useCallback(() => authFetch(`${API}/bulks/hrx`).then(async r => { if (r.ok) setSt(await r.json()); }).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const scan = useCallback(async (code: string) => {
    if (stopRef.current) return;                                    // the red stop must be acknowledged first
    const isBox = code.toUpperCase().replace(/[^A-Z0-9]/g, '').startsWith('WBX');
    const r = await authFetch(`${API}/bulks/hrx/scan`, { method: 'POST', body: JSON.stringify({ code, box_id: isBox ? undefined : activeRef.current?.id }) });
    if (!r.ok) {
      const d = await detailOf(r);
      if (d.wrong) setStop(d.text); else setLast({ ok: false, text: d.text });
      return;
    }
    const d = await r.json();
    setSt(d);
    if (d.box) {
      const b = (d.boxes as Box[]).find(x => x.id === d.box.id) || { ...d.box, parcels: 0 };
      setActive(b);
      setLast({ ok: true, text: `Filling ${b.country} box ${b.box_no}` });
    } else if (d.scanned) {
      setLast({ ok: true, text: `${d.scanned.order_number} → ${d.scanned.country} box ${d.scanned.box_no} ✓` });
    }
  }, []);

  useScanWedge(code => scan(code), { enabled: !done });    // the station scanner, with or without an Enter (28.10)

  const newBox = async (cc: string) => {
    setBusy(true);
    try {
      const r = await authFetch(`${API}/bulks/hrx/box`, { method: 'POST', body: JSON.stringify({ country: cc }) });
      if (!r.ok) { setLast({ ok: false, text: (await detailOf(r)).text }); return; }
      const d = await r.json();
      setSt(d);
      setLast({ ok: d.print?.printed === 'queued', text: d.print?.printed === 'queued'
        ? `${cc} box ${d.box.box_no}: label printing — stick it on the box and scan it`
        : `${cc} box ${d.box.box_no} made, but ${d.print?.message || 'the label did not print'}` });
    } finally { setBusy(false); }
  };
  const reprint = async (b: Box) => {
    const r = await reprintApproval.run(approver => authFetch(`${API}/bulks/hrx/box/${b.id}/reprint`, {
      method: 'POST', body: JSON.stringify(approver) }));
    if (!r) return;                                  // the approval was cancelled
    const d = await r.json().catch(() => ({} as any));
    setLast({ ok: r.ok && d.printed === 'queued', text: r.ok ? (d.printed === 'queued' ? `${b.country} box ${b.box_no}: label again` : d.message) : (d.detail || `HTTP ${r.status}`) });
  };
  const undo = async () => {
    const r = await authFetch(`${API}/bulks/hrx/undo`, { method: 'POST' });
    if (!r.ok) { setLast({ ok: false, text: (await detailOf(r)).text }); return; }
    setSt(await r.json()); setLast({ ok: true, text: 'Last parcel taken out — take it out of the box too' });
  };
  const takeOut = async (item_id: number, num: string) => {
    const r = await authFetch(`${API}/bulks/hrx/remove`, { method: 'POST', body: JSON.stringify({ item_id }) });
    if (!r.ok) { setLast({ ok: false, text: (await detailOf(r)).text }); return; }
    setSt(await r.json()); setLast({ ok: true, text: `${num} taken out of its box` });
  };
  const close = async (confirm: boolean) => {
    setBusy(true);
    try {
      const r = await authFetch(`${API}/bulks/hrx/close`, { method: 'POST', body: JSON.stringify({ confirm_missing: confirm }) });
      if (!r.ok) { setLast({ ok: false, text: (await detailOf(r)).text }); return; }
      setDone(await r.json()); setActive(null);
    } finally { setBusy(false); }
  };

  const boxesOf = (cc: string) => (st?.boxes || []).filter(b => b.country === cc);
  const activeNow = active && st ? (st.boxes.find(b => b.id === active.id) || active) : null;

  return (
    <div className="space-y-6">
      {reprintApproval.box}
      {stop && (
        <div className="fixed inset-0 z-50 bg-red-700 flex flex-col items-center justify-center gap-8 p-8 text-center">
          <div className="text-6xl font-black text-white">STOP</div>
          <div className="text-3xl font-bold text-white max-w-3xl">{stop}</div>
          <div className="text-xl text-red-100">Do not put this parcel in this box.</div>
          <button onClick={() => setStop(null)} className="px-10 py-5 rounded-2xl bg-white text-red-700 text-2xl font-black">OK — I took it out</button>
        </div>
      )}

      <div className="flex items-center gap-3">
        <span className="text-3xl font-black">Bring boxes</span>
        {st?.bulk && <span className="text-gray-400 text-lg">{st.bulk.code}</span>}
        <button onClick={onBack} className="ml-auto px-4 py-2 rounded-lg bg-gray-700 hover:bg-gray-600">Back</button>
      </div>

      {done ? (
        <div className="rounded-2xl border-2 border-emerald-600 bg-emerald-900/15 p-6 text-center space-y-3">
          <div className="text-3xl font-black text-emerald-200">{done.code}: {done.parcels} parcels left with HRX</div>
          <div className="text-gray-200 text-lg">{Object.entries(done.per_country || {}).map(([k, n]) => `${k} ${n} in ${done.boxes?.[k] || 0} box(es)`).join(' · ')}</div>
          {done.not_boxed > 0 && <div className="text-amber-200">{done.not_boxed} processed Bring parcel(s) were in no box — written on the shipment</div>}
          <button onClick={onBack} className="px-6 py-3 rounded-xl bg-gray-700 hover:bg-gray-600 text-lg">Done</button>
        </div>
      ) : (
        <>
          <div className={`rounded-2xl border-2 p-5 flex items-center gap-6 ${activeNow ? COLOR[activeNow.country] : 'border-gray-700 bg-[#1a1d27]'}`}>
            {activeNow ? (
              <>
                <div className="text-7xl font-black">{activeNow.country}</div>
                <div>
                  <div className="text-2xl font-bold">Box {activeNow.box_no} · {activeNow.parcels} parcel(s)</div>
                  <div className="text-gray-300">Scan each {COUNTRIES.find(c => c[0] === activeNow.country)?.[1]} parcel you put in it. Another box: scan its label.</div>
                </div>
              </>
            ) : <div className="text-xl text-gray-300">Scan the label of the box you are filling (or make a new box below).</div>}
          </div>
          {last && <div className={`text-2xl font-bold ${last.ok ? 'text-green-400' : 'text-red-400'}`}>{last.text}</div>}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {COUNTRIES.map(([cc, name]) => (
              <div key={cc} className={`rounded-2xl border p-4 ${COLOR[cc]}`}>
                <div className="flex items-baseline gap-3">
                  <span className="text-4xl font-black">{cc}</span>
                  <span className="text-gray-300">{name}</span>
                  <span className="ml-auto text-3xl font-black">{st?.per_country?.[cc] || 0}</span>
                </div>
                <div className="mt-2 space-y-1">
                  {boxesOf(cc).map(b => (
                    <div key={b.id} className="flex items-center gap-2 text-sm">
                      <span className={activeNow?.id === b.id ? 'font-bold text-white' : 'text-gray-300'}>Box {b.box_no}: {b.parcels}</span>
                      <button onClick={() => reprint(b)} className="ml-auto px-2 py-0.5 rounded bg-gray-700 hover:bg-gray-600 text-xs">Label again</button>
                    </div>
                  ))}
                </div>
                <button disabled={busy} onClick={() => newBox(cc)} className="mt-3 w-full px-4 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 font-semibold disabled:opacity-40">
                  New {cc} box</button>
              </div>
            ))}
          </div>

          {!!st?.problems?.length && (
            <div className="rounded-2xl border-2 border-red-600 bg-red-900/20 p-4 space-y-2">
              <div className="text-red-200 text-xl font-black">Take {st.problems.length === 1 ? 'this parcel' : 'these parcels'} out of the box — must not go:</div>
              {st.problems.map(p => (
                <div key={p.item_id} className="flex flex-wrap items-center gap-3">
                  <span className="text-gray-100">{p.why}</span>
                  <button onClick={() => takeOut(p.item_id, p.order_number)} className="px-4 py-2 rounded-lg bg-red-700 hover:bg-red-600 text-white font-bold">Taken out</button>
                </div>
              ))}
            </div>
          )}

          {st && st.missing.length > 0 && (
            <div className="rounded-2xl border border-amber-700 bg-amber-900/15 p-4">
              <div className="text-amber-200 font-bold mb-1">{st.missing.length} Bring parcel(s) in no box yet (processed, or packed in a running round) — find them:</div>
              <div className="text-gray-200 text-sm">{st.missing.map(m => `${m.order_number} (${m.country})`).join(', ')}</div>
            </div>
          )}

          <div className="flex flex-wrap gap-3">
            <button onClick={undo} className="px-4 py-3 rounded-xl bg-gray-700 hover:bg-gray-600">Take the last one out</button>
            <button disabled={busy || !st?.count} onClick={() => close(false)}
              className="px-8 py-3 rounded-2xl bg-emerald-600 hover:bg-emerald-500 disabled:opacity-30 text-xl font-black">
              HRX leaves — {st?.count ?? 0} parcel(s)</button>
            {st && st.missing.length > 0 && (
              <button disabled={busy || !st?.count} onClick={() => { if (window.confirm(`Send without the ${st.missing.length} parcel(s) in no box? They are written on the shipment.`)) close(true); }}
                className="px-6 py-3 rounded-2xl bg-amber-700 hover:bg-amber-600 disabled:opacity-30 font-bold">
                Leave anyway</button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
